import { z } from 'zod';
import type { UseFormReturn } from 'react-hook-form';
import type { Company, UserProfile } from '@/lib/types/core';
import { ProjectPermissionLevel } from '@/types/enums';
import type { useUserContext } from '@/hooks/use-user-context';
import {
  createPersonNameSchema,
  createPhoneSchema,
  createEmailSchema,
  createRoleSchema
} from '../../../assignments/components/shared/utils/validation-utils';

// Constants
export const NO_COMPANY_SENTINEL = 'NO_COMPANY';

// Assignment permission type
export interface AssignmentPermission {
  assignmentId: string;
  assignmentName: string;
  permissionLevel: typeof ProjectPermissionLevel[keyof typeof ProjectPermissionLevel];
  emailNotifications: boolean;
}

// Assignment list item type for dropdown
export interface AssignmentListItem {
  id: string;
  name: string;
  ordering_company_id: string | null;
  performing_company_id: string | null;
}

// Schema factories
export const baseUserFormSchemaFactory = (t: (_key: string, _options?: string | Record<string, unknown> | undefined) => string) => z.object({
  firstName: createPersonNameSchema(t, 'firstName', true),
  lastName: createPersonNameSchema(t, 'lastName', true),
  phoneNumber: createPhoneSchema(t, false),
  language: z.enum(['en', 'es', 'fr', 'sv']).optional(),
  app_role: createRoleSchema(t, ['USER', 'COMPANY_ADMIN', 'ADMIN'] as const),
  companyId: z.string().optional(),
  isRepresentative: z.boolean().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'PENDING']).optional(),
  title: z.string().optional(),
  profilePictureData: z.object({
    tempStoragePath: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
  }).optional(),
  assignmentPermissions: z.array(z.object({
    assignmentId: z.string(),
    assignmentName: z.string(),
    permissionLevel: z.enum(['OWNER', 'EDITOR', 'OPERATOR', 'VIEWER']),
    emailNotifications: z.boolean(),
  })).optional(),
});

export const createUserFormSchemaFactory = (t: (_key: string, _options?: string | Record<string, unknown> | undefined) => string) => baseUserFormSchemaFactory(t)
  .extend({
    email: createEmailSchema(t),
  });

export const editUserFormSchemaFactory = (t: (_key: string, _options?: string | Record<string, unknown> | undefined) => string) => baseUserFormSchemaFactory(t).extend({
  // email is not editable for existing users directly in this form for now
});

// Form data types
export type CreateUserFormData = z.infer<ReturnType<typeof createUserFormSchemaFactory>>;
export type EditUserFormData = z.infer<ReturnType<typeof editUserFormSchemaFactory>>;
export type UserFormData = CreateUserFormData | EditUserFormData;

// Props interfaces
export interface UsersActionDialogProps {
  userToEdit?: UserProfile | null;
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  onUserMutated?: (_user: UserProfile) => void;
  mode: 'create' | 'edit';
}

export interface ProfileFieldsSectionProps {
  form: UseFormReturn<CreateUserFormData | EditUserFormData>;
  isSubmittingForm: boolean;
  isEdit: boolean;
  mode: 'create' | 'edit';
  t: (_key: string, _options?: string | Record<string, unknown> | undefined) => string;
  duplicateEmailError?: boolean | undefined;
  onClearDuplicateEmailError?: (() => void) | undefined;
  userToEdit?: UserProfile | null | undefined;
}

export interface CompanyRoleSectionProps {
  form: UseFormReturn<CreateUserFormData | EditUserFormData>;
  isSubmittingForm: boolean;
  isEdit: boolean;
  companies: Company[] | null;
  companiesLoading: boolean;
  userContext: ReturnType<typeof useUserContext> | null;
  userToEdit?: UserProfile | null;
  canEditProfileFields: boolean;
  shouldShowStatusField: boolean;
  shouldMakeRoleEditable: boolean;
  t: (_key: string, _options?: string | Record<string, unknown> | undefined) => string;
}

export interface AssignmentPermissionsSectionProps {
  form: UseFormReturn<CreateUserFormData | EditUserFormData>;
  isSubmittingForm: boolean;
  isEdit: boolean;
  assignments: AssignmentListItem[];
  assignmentsLoading: boolean;
  assignmentPermissions: AssignmentPermission[];
  permissionsLoading: boolean;
  emailPreferencesLoading: boolean;
  syncingPermissions: boolean;
  syncProgress: string;
  canEditAssignmentPermissions: boolean;
  canEditEmailNotifications: boolean;
  addAssignmentPermission: (_assignmentId: string) => void;
  removeAssignmentPermission: (_assignmentId: string) => void;
  updateAssignmentPermissionLevel: (_assignmentId: string, _level: string) => void;
  updateEmailNotificationPreference: (_assignmentId: string, _enabled: boolean) => void;
  t: (_key: string, _options?: string | Record<string, unknown> | undefined) => string;
}